import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { Banner, BannerDescription } from '@wartek-id/banner'
import { Button } from '@wartek-id/button-v1'
import { Link } from '@wartek-id/link'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import {
  useAPIGetListRealisasiV2,
  useAPIPelaksanaanProgress,
} from 'api/kinerja/useAPIKinerja'
import PanelSummary from 'app/KinerjaPage/components/PanelSummary'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaPage/components/TablePelaksanaanKinerja'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { formatDate } from 'utils/datetime'
import DialogConfirmEdit from './components/DialogConfirmEdit'
import { getBadgeByStateV2, getCtaByState } from './utils'
import { Header } from 'app/KinerjaPage/pages/PelaksanaanV2/components/Header'
import { PENGEMBANGAN_KOMPETENSI_REALISASI_RESULT } from 'app/KinerjaShared/constants/pengembanganKompetensi'

const LoadingState = () => {
  return (
    <div className="flex justify-center p-6" data-testid="loading-spinner">
      <Spinner />
    </div>
  )
}

type PengembanganKompetensiProps = {
  overridePeriodPayload?: boolean
  isViewOnly?: boolean
  period?: number
  year?: number
  resolveBuktiDukungUrl?: (id: string, sequence: number) => string
}
const defaultBuktiDukungUrlResolver = (id: string, sequence: number) =>
  `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/pengembangan-kompetensi/${id}/bukti-dukung?sequence=${sequence}`

const PengembanganKompetensi = ({
  isViewOnly = false,
  overridePeriodPayload = false,
  period,
  year,
  resolveBuktiDukungUrl = defaultBuktiDukungUrlResolver,
}: PengembanganKompetensiProps) => {
  const periodParams = overridePeriodPayload
    ? { skp_period: period, skp_year: year }
    : undefined

  const { push } = useRouter()
  const { data, isLoading } = useAPIGetListRealisasiV2('PK', periodParams)
  const { data: progressData } = useAPIPelaksanaanProgress({ periodParams })
  const currentPeriod = progressData?.data?.period ?? 1

  const isEvaluated = progressData?.data?.evaluationDocumentState === 'READY'

  const dataKinerjaOrdered = data?.data ?? []
  const kinerjaUpdatedDate = data?.kinerjaUpdated?.find(
    (e) => e.kategori === 'PK'
  )

  const targetTotal = data?.bobotTotal ?? 0
  const realisasiTotal = data?.realisasiTotal ?? 0

  const realisasiResultBgColor =
    PENGEMBANGAN_KOMPETENSI_REALISASI_RESULT[data?.realisasiResult]?.className
  let realisasiResultLabel =
    PENGEMBANGAN_KOMPETENSI_REALISASI_RESULT[data?.realisasiResult]?.label

  const guideLink =
    'https://docs.google.com/document/d/1G4WV_rXbJh4CoKg45c7eKeyyHY8wR8pVwwdVRjz9j90'

  const [showConfirmEditRHK, setShowConfirmEditRHK] = useState(false)

  return (
    <div className="px-8 pb-8 pt-12">
      <div className="max-w-7xl mx-auto">
        <Header
          title="Pengembangan Kompetensi"
          className="mb-9"
          rightNode={
            <PanelSummary
              items={[
                {
                  label: 'Semester',
                  value: currentPeriod,
                },
              ]}
            />
          }
        />

        <Banner
          className="mb-9"
          animationDuration={300}
          title="Poin dan kelengkapan dokumen tidak berpengaruh langsung ke Predikat Kinerja"
          type="informational"
          closable
        >
          <BannerDescription>
            Jika total poin belum memenuhi ekspektasi, Anda tidak perlu khawatir
            dan dapat tetap fokus mengembangkan kompetensi sesuai rencana. Yang
            terpenting adalah upaya dan progres Anda dalam mengembangkan diri,
            bukan angka dan poin yang diperoleh.
          </BannerDescription>
        </Banner>

        <div className="bg-white rounded-lg p-6">
          <div className="flex">
            <div className="flex flex-col gap-1 flex-1">
              <Text variant="heading-md">Progres pengumpulan dokumen</Text>
              <Text variant="body" color="subdued">
                Kumpulkan berdasarkan Perencanaan Kinerja yang Anda dan atasan
                sudah sepakati.
              </Text>
            </div>

            <Button
              color="white"
              disabled={isEvaluated || isViewOnly}
              onClick={() => {
                if (!isEvaluated && !isViewOnly) {
                  setShowConfirmEditRHK(true)
                }
              }}
            >
              Ubah Rencana Hasil Kerja
            </Button>
          </div>

          {isLoading ? (
            <LoadingState />
          ) : (
            <>
              <TablePelaksanaanKinerja data-testid="table-kompetensi">
                <TableHeader>
                  <TableHeaderItem className="w-5">No.</TableHeaderItem>
                  <TableHeaderItem className="pl-14 w-[500px]">
                    Rencana Hasil Kerja
                  </TableHeaderItem>
                  <TableHeaderItem>Target</TableHeaderItem>
                  <TableHeaderItem>Realisasi</TableHeaderItem>
                  <TableHeaderItem>Status</TableHeaderItem>
                  <TableHeaderItem />
                </TableHeader>

                <TableBody>
                  {dataKinerjaOrdered.map((kinerja, index) => (
                    <TableRow key={`row-kinerja-${kinerja.id}`}>
                      <TableRowItem className="text-center">
                        {index + 1}
                      </TableRowItem>
                      <TableRowItem className="pl-14">
                        {kinerja.deskripsi}
                      </TableRowItem>
                      <TableRowItem>{kinerja.bobot ?? 0} poin</TableRowItem>
                      <TableRowItem>{kinerja.realisasi ?? 0} poin</TableRowItem>
                      <TableRowItem>
                        {getBadgeByStateV2(kinerja.state)}
                      </TableRowItem>
                      <TableRowItem>
                        <NextLink
                          href={resolveBuktiDukungUrl(kinerja.id, index + 1)}
                          passHref
                        >
                          {getCtaByState(kinerja.state, isViewOnly)}
                        </NextLink>
                      </TableRowItem>
                    </TableRow>
                  ))}
                  <TableRow className={realisasiResultBgColor}>
                    <TableRowItem />
                    <TableRowItem className="pl-14">
                      <div className="flex flex-col gap-1">
                        <Text variant="body-sm-bold">TOTAL POIN</Text>
                        <Text color="subdued" variant="body-sm">
                          Cek selengkapnya pada
                          <Link href={guideLink} target="_blank" rel="noopener">
                            peraturan yang berlaku.
                          </Link>
                        </Text>
                      </div>
                    </TableRowItem>
                    <TableRowItem>
                      <Text variant="body-sm-bold">{targetTotal} poin</Text>
                    </TableRowItem>
                    <TableRowItem colSpan={3}>
                      <Text data-testid="total-point" variant="body-sm-bold">
                        {realisasiTotal} poin ({realisasiResultLabel})
                      </Text>
                    </TableRowItem>
                  </TableRow>
                </TableBody>
              </TablePelaksanaanKinerja>
            </>
          )}
        </div>
        {kinerjaUpdatedDate && (
          <div className="mt-5 bg-white px-3 py-1 flex justify-center rounded-md">
            <Text variant="body" color="subdued">
              Rencana Hasil Kerja diubah pada:{' '}
              {formatDate(kinerjaUpdatedDate.kinerjaUpdatedAt, 'd MMMM yyyy')}
            </Text>
          </div>
        )}

        <DialogConfirmEdit
          data-testid="dialog-confirm-edit-rhk"
          customSize={595}
          isOpen={showConfirmEditRHK}
          onClose={() => setShowConfirmEditRHK(false)}
          onConfirm={() => {
            push(
              `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/pengembangan-kompetensi/edit`
            )
          }}
        />
      </div>
    </div>
  )
}

export default PengembanganKompetensi
